<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { GripVertical } from 'lucide-vue-next';
  import { IconImage } from '@/shared/ui';
  import { SearchCombobox } from '@/shared/ui/search-combobox';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import { formatFieldOptionLabel } from '../../lib/matrix-fields';
  import type { ArchivingField } from '../../model/archiving.types';

  const SEARCH_PLACEHOLDER = 'Buscar pelo ID do campo';
  const EMPTY_MESSAGE = 'Nenhum campo encontrado.';

  const props = withDefaults(
    defineProps<{
      label: string;
      modelValue: string;
      options: ArchivingField[];
      canAdd: boolean;
      canRemove: boolean;
      showHandle: boolean;
      addDisabled?: boolean;
      placeholder?: string;
    }>(),
    { addDisabled: false, placeholder: 'Selecione um campo' }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    add: [];
    remove: [];
    search: [term: string];
  }>();

  const labelId = useId();

  const comboboxOptions = computed(() =>
    props.options.map((field) => ({ value: field.name, label: formatFieldOptionLabel(field) }))
  );
</script>

<template>
  <div class="flex flex-col gap-2" data-testid="archiving-route">
    <span
      :id="labelId"
      class="text-sm font-semibold text-gray-07"
      data-testid="archiving-route-label"
    >
      {{ label }}
    </span>

    <div class="flex items-center gap-3">
      <span
        v-if="showHandle"
        class="drag-handle flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-gray-03 hover:bg-[var(--theme-secondary-tint)] hover:text-[var(--theme-secondary-ink)]"
        aria-hidden="true"
        data-testid="archiving-route-handle"
      >
        <GripVertical class="size-5" />
      </span>

      <SearchCombobox
        :model-value="modelValue"
        :options="comboboxOptions"
        :aria-labelledby="labelId"
        :placeholder="placeholder"
        :search-placeholder="SEARCH_PLACEHOLDER"
        :empty-message="EMPTY_MESSAGE"
        trigger-icon="search"
        class="min-w-0 flex-1 bg-white-01"
        @update:model-value="emit('update:modelValue', $event)"
        @search="emit('search', $event)"
      />

      <button
        v-if="canAdd"
        type="button"
        aria-label="Adicionar rota"
        :disabled="addDisabled"
        class="flex shrink-0 items-center justify-center text-[var(--theme-secondary)]"
        :class="addDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
        data-testid="archiving-route-add"
        @click="emit('add')"
      >
        <IconImage :src="AddButton" alt="Adicionar rota" />
      </button>
      <button
        v-if="canRemove"
        type="button"
        :aria-label="`Remover ${label}`"
        class="flex shrink-0 cursor-pointer items-center justify-center text-[var(--theme-secondary)]"
        data-testid="archiving-route-remove"
        @click="emit('remove')"
      >
        <IconImage :src="DeleteButton" :alt="`Remover ${label}`" />
      </button>
    </div>
  </div>
</template>
